import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import type { SetCard as SetCardType } from '../api/workoutCards';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { hapticLight, hapticSuccess, hapticMedium } from '../../../lib/haptics';

interface SetCardProps {
  card: SetCardType;
  onUpdate: (field: 'weight' | 'repsDone' | 'notes', value: string) => void;
  onComplete: () => void;
}

export function SetCard({ card, onUpdate, onComplete }: SetCardProps) {
  const [showHistory, setShowHistory] = useState(false);

  // Local state for responsive typing - only persist to Firebase on blur
  // Default reps to repsPlanned so users can quickly complete sets
  const [localWeight, setLocalWeight] = useState<string>(card.set.weight?.toString() || '');
  const [localReps, setLocalReps] = useState<string>(
    card.set.repsDone?.toString() || card.repsPlanned.toString()
  );
  const [localNotes, setLocalNotes] = useState<string>(card.set.notes || '');

  // Sync local state when card changes (e.g., navigating between sets)
  useEffect(() => {
    setLocalWeight(card.set.weight?.toString() || '');
    setLocalReps(card.set.repsDone?.toString() || card.repsPlanned.toString());
    setLocalNotes(card.set.notes || '');
  }, [card.id, card.set.weight, card.set.repsDone, card.set.notes, card.repsPlanned]);

  const adjustWeight = (delta: number) => {
    const current = card.set.weight || card.lastWeightSuggestion || 0;
    const newWeight = Math.max(0, current + delta);
    setLocalWeight(newWeight.toString());
    onUpdate('weight', newWeight.toString());
    hapticLight();
  };

  const handleComplete = () => {
    // Flush any pending local state before completing
    if (localWeight !== (card.set.weight?.toString() || '')) {
      onUpdate('weight', localWeight);
    }
    if (localReps !== (card.set.repsDone?.toString() || '')) {
      onUpdate('repsDone', localReps);
    }
    if (localNotes !== (card.set.notes || '')) {
      onUpdate('notes', localNotes);
    }
    hapticSuccess();
    onComplete();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <Card padding="lg" variant="elevated" className="h-full flex flex-col justify-between space-y-6">
        <div className="text-center space-y-3">
          <Badge variant="success" size="md">
            Set {card.setIndex}
          </Badge>

          <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
            {card.exerciseName}
          </h3>

          <p className="text-lg text-gray-600 dark:text-slate-400">
            Target: {card.repsPlanned} reps
          </p>
        </div>

        <div className="flex-1 flex flex-col justify-center space-y-4 max-w-md mx-auto w-full">
          {/* Weight Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
              Weight (lb)
            </label>

            {/* Weight input with centered buttons */}
            <div className="flex flex-col gap-2">
              <input
                type="number"
                inputMode="decimal"
                className="form-input text-2xl text-center font-bold w-full max-w-none"
                placeholder={card.lastWeightSuggestion?.toString() || '0'}
                value={localWeight}
                onChange={(e) => {
                  setLocalWeight(e.target.value);
                  hapticLight();
                }}
                onBlur={() => onUpdate('weight', localWeight)}
              />

              {/* Quick adjust buttons - two rows */}
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => adjustWeight(-10)}
                  className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-slate-300 transition text-sm font-medium"
                  type="button"
                >
                  -10
                </button>
                <button
                  onClick={() => adjustWeight(-5)}
                  className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-slate-300 transition text-sm font-medium"
                  type="button"
                >
                  -5
                </button>
                <button
                  onClick={() => adjustWeight(5)}
                  className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-slate-300 transition text-sm font-medium"
                  type="button"
                >
                  +5
                </button>
                <button
                  onClick={() => adjustWeight(10)}
                  className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-slate-300 transition text-sm font-medium"
                  type="button"
                >
                  +10
                </button>
              </div>
            </div>

            {card.lastWeightSuggestion && (
              <div className="flex justify-center gap-2 items-center">
                <p className="text-xs text-gray-500 dark:text-slate-500">
                  Last: {card.lastWeightSuggestion} lb
                </p>
                {/* Show PR indicator if current weight exceeds all recent logs */}
                {localWeight && Number(localWeight) > Math.max(...card.recentLogs.map(l => l.weight || 0), 0) && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-yellow-600 dark:text-yellow-400">
                    PR
                  </span>
                )}
                {!localWeight && (
                  <button
                    onClick={() => {
                      const val = card.lastWeightSuggestion!.toString();
                      setLocalWeight(val);
                      onUpdate('weight', val);
                      hapticMedium();
                    }}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    type="button"
                  >
                    Use last
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Reps Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
              Reps Completed
            </label>
            <input
              type="number"
              inputMode="numeric"
              className="form-input text-2xl text-center font-bold w-full max-w-none"
              placeholder={card.repsPlanned.toString()}
              value={localReps}
              onChange={(e) => {
                setLocalReps(e.target.value);
                hapticLight();
              }}
              onBlur={() => onUpdate('repsDone', localReps)}
            />
          </div>

          {/* Notes Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
              Notes (optional)
            </label>
            <textarea
              className="form-textarea text-sm"
              placeholder="How did this set feel?"
              rows={2}
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              onBlur={() => onUpdate('notes', localNotes)}
            />
          </div>

          {/* History Toggle */}
          {card.recentLogs.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              {showHistory ? 'Hide' : 'Show'} recent history
            </button>
          )}

          {showHistory && card.recentLogs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="space-y-1 text-xs text-gray-600 dark:text-slate-400">
                {card.recentLogs.slice(0, 3).map((log) => (
                  <div key={log.id} className="flex justify-between">
                    <span>
                      {log.performedAt?.toDate().toLocaleDateString()}
                    </span>
                    <span className="font-mono">
                      {log.weight ? `${log.weight} lb` : '-'} × {log.reps || '-'} reps
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        <Button
          variant="success"
          size="lg"
          onClick={handleComplete}
          className="w-full text-xl py-4"
        >
          Complete Set
        </Button>
      </Card>
    </motion.div>
  );
}
